import "../../order-create-form.scss";

import React, { useEffect, useState, FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, isArray, cloneDeep } from "lodash";
import { ToTitleCase } from "Helpers/to-title-case";
import { Col, Row, Divider, Skeleton } from "antd";
import { Button } from "@life-line-lab/components-react";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { ParcelTypeEnums } from "Data/order";
import { formatDate } from "Helpers/format-date";
import useOrderFormStates from "../../order-form-states";

interface ConfirmOrder {
  info?: any;
  onPrev?: any;
  handleSubmit?: any;
  parentInput?: any;
  isLoading: boolean;
  deliveryServiceTypes:any[]
}

const BaseChargeType = ["DELIVERY_TYPE", "SERVICE_TYPE"];

const useSeparateCharges = (order, childrenOrders) => {
  const constructChargeGroup = (additionalCharges, parentCharge) => {
    if (!isArray(additionalCharges)) return {};

    const chargeCopy = { ...parentCharge };
    const { baseChargesGroup = {}, additionalChargesGroup = {} } =
      chargeCopy || {};

    let charges: any = {
      baseChargesGroup,
      additionalChargesGroup,
    };

    // for each estimatedAdditionalCharges, calculate
    additionalCharges.forEach((c) => {
      const chargeGroupName = BaseChargeType.includes(c.type)
        ? "baseChargesGroup"
        : "additionalChargesGroup";

      if (!charges?.[chargeGroupName]?.[c.name]) {
        charges = {
          ...charges,
          [chargeGroupName]: {
            ...charges?.[chargeGroupName],
            [c.name]: c.amount,
          },
        };
        return;
      }

      let tCharge = parseFloat(charges?.[chargeGroupName]?.[c.name]);

      // sum up the charges
      tCharge = tCharge + parseFloat(c.amount);

      // 4 to prevent inaccurate calculations
      charges = {
        ...charges,
        [chargeGroupName]: {
          ...charges?.[chargeGroupName],
          [c.name]: tCharge.toFixed(4),
        },
      };
    });

    return charges;
  };

  const separateCharges = useMemo(() => {
    let charges: any = {
      baseCharge: "0",
      baseChargesGroup: {},
      baseChargesPackageGroup: {},
      additionalCharge: "0",
      additionalChargesGroup: {},
      totalCharge: "0",
      totalChargeGroup: {},
    };

    if (!order) return charges;
    
    const additionalCharges = order?.estimatedAdditionalCharges || [];
    charges.baseCharge = order.estimatedBaseCharges;
    charges.additionalCharge = order.estimatedAdditionalCharges;
    charges.totalCharge = order.estimatedTotalCharges;

    const result = constructChargeGroup(additionalCharges, charges);

    charges = {
      ...charges,
      ...result,
    };

    if (order.orderType === ParcelTypeEnums.SPLIT && childrenOrders) {
      childrenOrders.forEach((child: any, index: number) => {
        if (child?.estimatedBaseCharges) {
          charges.baseChargesPackageGroup[`Package ${index + 1}`] =
            child.estimatedBaseCharges;
        }
      });
    }

    return charges;
  }, [order, childrenOrders]);

  return separateCharges;
};

const SummaryCol: FC<any> = ({ label, content, allLabel = false }: any) => {
  // details show extra information if needed, such as dimensions format, charges (fixed or variable, at some amount
  return (
    <>
      <Col span={9} className="mb-custom-0">
        <Row>
          <Col span={24} className="oc-label mb-custom-0">
            {label}
          </Col>
        </Row>
      </Col>
      <Col
        span={15}
        className={allLabel ? "oc-label mb-custom-0" : "mb-custom-0"}
      >
        {content}
      </Col>
    </>
  );
};

const ExtendedParcelInformation: FC<any> = (props) => {
  const { orders = [], t } = props || {};
  // normalise orders
  const normalisedOrders: any[] = useMemo(() => {
    if (!isArray(orders)) return [];

    let allParcels: any = [];

    orders.forEach((o) => {
      if (isArray(o.parcels)) allParcels = [...allParcels, ...o.parcels];
    });

    return allParcels;
  }, [orders]);

  return (
    <Col
      span={24}
      className="details-container order-create-container mb-custom-0"
    >
      <div className="mini-row">
        <div className="details-title">{t("title.parcelInfo")}</div>
        <div className="details-content ocf-parcel-container">
          {normalisedOrders.map((parcel, index) => {
            const isNotFinalParcel =
              index === normalisedOrders.length - 1 ? false : true;

            return (
              <React.Fragment key={index}>
                <Row gutter={[8, 8]}>
                  <SummaryCol
                    label={"Type of Goods: "}
                    content={parcel?.name}
                  />
                  <SummaryCol
                    label={"Goods Value: "}
                    content={parcel?.goodsValue}
                  />
                </Row>
                {isNotFinalParcel && <div className="logistics-divider" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </Col>
  );
};

const ExtendedOrderInformation: FC<any> = (props) => {
  const { order, orderType, t } = props || {};

  return (
    <Col
      span={24}
      className="details-container order-create-container mb-custom-0"
    >
      <div className="mini-row">
        <div className="details-title">{t("title.orderInformation")}</div>
        <div className="details-content">
          <Row gutter={[8, 8]}>
            <SummaryCol
              label={"Order Type: "}
              content={order?.looseOrders ? "Loose" : t(`option.${orderType}`)}
            />
            {orderType !== ParcelTypeEnums.SPLIT && (
              <>
                <SummaryCol label={"Weight Type: "} content={order?.weight} />
                <SummaryCol
                  label={"Dimension: "}
                  content={`${order?.dimension.length} x ${order?.dimension.width} x ${order?.dimension.height}`}
                  details={["l x w x h"]}
                />
                <SummaryCol
                  label={"Remarks: "}
                  content={order.remarks ? order?.remarks : "No remarks."}
                />
                <SummaryCol
                  label={"Order Status: "}
                  content={`${
                    order?.orderStatusInfo.orderStatus.charAt(0) +
                    order?.orderStatusInfo.orderStatus
                      .toLowerCase()
                      .substring(1)
                      .replaceAll("_", " ")
                  } upon confirmation.`}
                />
                {order.orderStatusInfo.issueType
                  ? order?.orderStatusInfo.issueType.map((issue, index) => (
                      <SummaryCol
                        key={index}
                        label={"Issue: "}
                        content={`${issue?.settingAffected?.charAt(
                          0,
                        )}${issue?.settingAffected
                          ?.substring(1)
                          .toLowerCase()
                          .replaceAll("_", " ")} - ${issue?.name}`}
                      />
                    ))
                  : null}
              </>
            )}
          </Row>
        </div>
      </div>
    </Col>
  );
};

const ExtendedPackageInformation: FC<any> = (props) => {
  const { orders = [], t } = props || {};

  if (!isArray(orders)) return <></>;

  return (
    <Col
      span={24}
      className="details-container order-create-container mb-custom-0"
    >
      <div className="mini-row">
        <div className="details-title">{t("title.packageInformation")}</div>
        <div className="details-content ocf-package-container">
          {orders.map((o, i) => (
            <div className="ocf-package-row" key={i}>
              <div className="ocf-p-header">
                <div className="ocf-p-title">{t("label.package")} {i + 1}</div>
              </div>
              <div className="ocf-p-container">
                <Row gutter={[8, 8]}>
                  <SummaryCol label={"Weight Type: "} content={o?.weight} />
                  <SummaryCol
                    label={"Dimension: "}
                    content={`${o?.dimension.length} x ${o?.dimension.width} x ${o?.dimension.height}`}
                    details={["l x w x h"]}
                  />
                  <SummaryCol
                    label={"Remarks: "}
                    content={o.remarks ? o?.remarks : "No remarks."}
                  />
                  <SummaryCol
                    label={"Order Status: "}
                    content={`${
                      o?.orderStatusInfo.orderStatus.charAt(0) +
                      o?.orderStatusInfo.orderStatus
                        .toLowerCase()
                        .substring(1)
                        .replaceAll("_", " ")
                    } upon confirmation.`}
                  />
                  {o.orderStatusInfo.issueType &&
                    o?.orderStatusInfo.issueType.map((issue, index) => (
                      <SummaryCol
                        key={index}
                        label={"Issue: "}
                        content={`${issue?.settingAffected?.charAt(
                          0,
                        )}${issue?.settingAffected
                          ?.substring(1)
                          .toLowerCase()
                          .replaceAll("_", " ")} - ${issue?.name}`}
                      />
                    ))}
                </Row>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Col>
  );
};

const ExtendedChargesInformation: FC<any> = (props) => {
  const { allCharges = {} } = props || {};

  const {
    baseCharge = "0",
    baseChargesGroup = [],
    additionalCharge = "0",
    additionalChargesGroup = [],
    totalCharge = "0",
    baseChargesPackageGroup = {},
  } = allCharges || {};

  return (
    <Col
      span={24}
      className="details-container order-create-container mb-custom-0"
    >
      <div className="mini-row">
        <div className="details-title">BASE CHARGES</div>
        <div className="details-content">
          <Row gutter={[8, 8]}>
            <SummaryCol
              label={"Base Charges: "}
              content={`RM ${parseFloat(baseCharge).toFixed(2)}`}
            />
            {!isEmpty(baseChargesPackageGroup) && (
              <Col span={24}>
                <div className="occ-base-charges-packages">
                  <Row gutter={[8, 8]}>
                    {Object.keys(baseChargesPackageGroup).map(
                      (bName, index) => (
                        <SummaryCol
                          key={index}
                          label={"- " + bName + ":"}
                          content={`RM ${parseFloat(
                            baseChargesPackageGroup[bName],
                          ).toFixed(2)}`}
                        />
                      ),
                    )}
                  </Row>
                </div>
              </Col>
            )}
            {Object.keys(baseChargesGroup)?.map((cName, index) => (
              <SummaryCol
                key={index}
                label={ToTitleCase(cName) + ":"}
                content={`RM ${parseFloat(baseChargesGroup[cName]).toFixed(2)}`}
              />
            ))}
          </Row>
        </div>
      </div>
      {!isEmpty(additionalChargesGroup) && (
        <div className="mini-row">
          <div className="details-title">ADDITIONAL CHARGES</div>
          <div className="details-content">
            <Row gutter={[8, 8]}>
              <Col span={24} className="mb-custom-0">
                <Row gutter={[8, 8]}>
                  {Object.keys(additionalChargesGroup)?.map((cName, index) => (
                    <SummaryCol
                      key={index}
                      label={ToTitleCase(cName) + ":"}
                      content={`RM ${parseFloat(
                        additionalChargesGroup[cName],
                      ).toFixed(2)}`}
                    />
                  ))}
                </Row>
              </Col>
            </Row>
          </div>
        </div>
      )}
      <div className="mini-row">
        <Row>
          <Divider />
          <SummaryCol
            label={"Total Charges: "}
            content={`RM ${parseFloat(totalCharge).toFixed(2)}`}
          />
        </Row>
      </div>
    </Col>
  );
};

const ConfirmOrderInfo: FC<ConfirmOrder> = ({
  info,
  onPrev,
  handleSubmit,
  parentInput,
  isLoading,
  deliveryServiceTypes,
}) => {
  const { t } = useTranslation();
  const initialInput = {};

  const [input, setInput] = useState<any>(initialInput);

  const { estimatedDeliveryDays } = useOrderFormStates();

  useEffect(() => {
    if (!isEmpty(parentInput)) {
      setInput({ ...initialInput });
    }
  }, [parentInput]);

  // Destructure info for order and childrenOrders
  const { order, childrenOrders } = info || {};

  // Note:
  //   -> These are needed to be destructured because in DB they fall under additionalCharges, but the client wants them under base charges.
  const allCharges = useSeparateCharges(order, childrenOrders);

  const orderType = order?.orderType;

  const extractDeliveryDate = ()=> {
    const deliveryDate = childrenOrders ? childrenOrders[0]?.deliveryDate : order?.deliveryDate;
    return formatDate(deliveryDate, "DD-MM-YYYY");
  }

  const extractServiceType = () => {
    const match = deliveryServiceTypes.find(
      (deliveryType) => deliveryType.id === order.deliveryType,
    );
    return match?.subOptions.find(
      (subOption) => subOption.id === order.serviceType,
    )?.label;
  };
  
  return (
    <>
      {
        <div className="center header-title">
          {t("title.confirmOrder").toUpperCase()}
        </div>
      }
      {isEmpty(order) && <Skeleton />}
      {!isEmpty(order) && (
        <Row gutter={[16, 8]} className="order-check">
          {order?.orderStatusInfo?.issueType && (
            <Col span={24}>
              <div className="oc-info oc-help">
                <ExclamationCircleOutlined style={{ fontSize: "16px " }} />
                <div>
                  Issues detected. You may settle them later under Diolko
                  Review. They are listed under the Order Information below.
                </div>
              </div>
            </Col>
          )}
          <Col span={24}>
            <Row gutter={[0, 16]}>
              <Col
                span={24}
                className="details-container order-create-container mb-custom-0"
              >
                <div className="mini-row">
                  <div className="details-title">{t("title.senderInformation")}</div>
                  <div className="details-content">
                    <Row gutter={[8, 8]}>
                      <SummaryCol
                        label={"Full Name: "}
                        content={order?.senderInfo?.fullName}
                      />
                      <SummaryCol
                        label={"Contact Email: "}
                        content={order?.senderInfo?.contactEmail}
                      />
                      <SummaryCol
                        label={"Contact Number: "}
                        content={order?.senderInfo?.contactNumber?.fullNumber}
                      />
                      <SummaryCol
                        label={"Address: "}
                        content={`${order?.senderInfo?.address?.street1},                                                
                                ${order?.senderInfo?.address?.city},
                                ${order?.senderInfo?.address?.postcode}  
                                ${order?.senderInfo?.address?.state}, 
                                ${order?.senderInfo?.address?.country}`}
                      />
                    </Row>
                  </div>
                </div>
              </Col>
              <Col
                span={24}
                className="details-container order-create-container mb-custom-0"
              >
                <div className="mini-row">
                  <div className="details-title">{t("title.receiverInfo")}</div>
                  <div className="details-content">
                    <Row gutter={[8, 8]}>
                      <SummaryCol
                        label={"Full Name: "}
                        content={order?.receiverInfo?.fullName}
                      />
                      <SummaryCol
                        label={"Contact Email: "}
                        content={order?.receiverInfo?.contactEmail}
                      />
                      <SummaryCol
                        label={"Contact Number: "}
                        content={order?.receiverInfo?.contactNumber?.fullNumber}
                      />
                      <SummaryCol
                        label={"Address: "}
                        content={`${order?.receiverInfo?.address?.street1},
                                ${
                                  order?.receiverInfo?.address?.street2 &&
                                  order?.receiverInfo?.address?.street2 + ", "
                                }                                                
                                ${order?.receiverInfo?.address?.city}, 
                                ${order?.senderInfo?.address?.postcode} 
                                ${order?.receiverInfo?.address?.state}, 
                                ${order?.receiverInfo?.address?.country}`}
                      />
                      <SummaryCol
                        label={"Coordinates: "}
                        content={`${order?.receiverInfo?.address?.coordinate.lat}, ${order?.receiverInfo?.address?.coordinate.lon}`}
                      />
                    </Row>
                  </div>
                </div>
              </Col>
              <Col
                span={24}
                className="details-container order-create-container mb-custom-0"
              >
                <div className="mini-row">
                  <div className="details-title">{t("title.deliveryInfo")}</div>
                  <div className="details-content">
                    <Row gutter={[8, 8]}>
                      <SummaryCol
                        label={"Service Type: "}
                        content={extractServiceType()}
                      />
                      <SummaryCol
                        label={"Estimated Delivery (Days): "}
                        content={estimatedDeliveryDays}
                      />
                      <SummaryCol
                        label={"Delivery Date: "}
                        content={extractDeliveryDate()}
                      />
                    </Row>
                  </div>
                </div>
              </Col>
              <ExtendedParcelInformation
                orders={
                  orderType === ParcelTypeEnums.SPLIT ? childrenOrders : [order]
                }
                t={t}
              />
              <ExtendedOrderInformation order={order} orderType={orderType} t={t} />
              {orderType === ParcelTypeEnums.SPLIT && (
                <ExtendedPackageInformation orders={childrenOrders} t={t} />
              )}
              <ExtendedChargesInformation allCharges={allCharges} />
            </Row>
          </Col>
        </Row>
      )}
      <div className="steps-action center mt-50">
        <Button
          onClick={() => onPrev(input)}
          type="ghost"
          label={t("action.back")}
          className="mr-10"
          isDisabled={isLoading}
        />
        <Button
          type="primary"
          onClick={() => handleSubmit(info)}
          label={t("action.submit")}
          isLoading={isLoading}
        />
      </div>
    </>
  );
};

export default ConfirmOrderInfo;
